import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Send, Users, Clock, CheckCircle, AlertCircle, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Announcements = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canSendAnnouncements = isAdmin || hasPermission('send_announcements');
  const [announcements, setAnnouncements] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTestSmsOpen, setIsTestSmsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    send_to: 'all'
  });
  const [testSmsData, setTestSmsData] = useState({
    phone_number: '',
    message: 'This is a test message from Mipay. If you received this, SMS is working!'
  });

  useEffect(() => {
    fetchAnnouncements();
    fetchSubscribers();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`);
      setAnnouncements(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/subscribers`);
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.send_to === 'selected' && selectedSubscribers.length === 0) {
      toast.error('Please select at least one subscriber');
      return;
    }
    
    setSending(true);
    try {
      const payload = {
        ...formData,
        selected_subscriber_ids: formData.send_to === 'selected' ? selectedSubscribers.map(s => s.id) : []
      };
      const response = await axios.post(`${API}/announcements/send`, payload);
      
      // Check if it was a duplicate
      if (response.data.duplicate) {
        toast.warning(`Duplicate detected! Same message was already sent ${response.data.original_announcement?.recipient_count || 0} subscriber(s) at ${new Date(response.data.original_announcement?.created_at).toLocaleString()}`);
      } else {
        const skipped = response.data.skipped_count || 0;
        const msg = skipped > 0 
          ? `Announcement sent to ${response.data.sent_count} subscriber(s)! (${skipped} skipped - no mobile or duplicate numbers)`
          : `Announcement sent to ${response.data.sent_count} subscriber(s)!`;
        toast.success(msg);
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error sending announcement:', error);
      toast.error('Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      send_to: 'all'
    });
    setSelectedSubscribers([]);
    setSearchTerm('');
  };

  const handleTestSms = async (e) => {
    e.preventDefault();
    if (!testSmsData.phone_number || !testSmsData.message) {
      toast.error('Please enter phone number and message');
      return;
    }
    
    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/sms/test`, testSmsData);
      toast.success(response.data.message);
      setIsTestSmsOpen(false);
      setTestSmsData({
        phone_number: '',
        message: 'This is a test message from Mipay. If you received this, SMS is working!'
      });
    } catch (error) {
      console.error('Error sending test SMS:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleSubscriber = (subscriber) => {
    setSelectedSubscribers(prev => {
      const exists = prev.find(s => s.id === subscriber.id);
      if (exists) {
        return prev.filter(s => s.id !== subscriber.id);
      } else {
        return [...prev, subscriber];
      }
    });
  };

  const removeSubscriber = (subscriberId) => {
    setSelectedSubscribers(prev => prev.filter(s => s.id !== subscriberId));
  };

  const filteredSubscribers = subscribers.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.mobile.includes(searchTerm)
  );

  const getSendToLabel = (sendTo, recipientNames) => {
    switch (sendTo) {
      case 'all':
        return 'All Subscribers';
      case 'active':
        return 'Active Subscribers';
      case 'overdue':
        return 'Overdue Subscribers';
      case 'selected':
        return recipientNames || 'Selected Subscribers';
      default:
        return 'All Subscribers';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-jakarta font-bold text-slate-900">Broadcast Messages</h2>
          <p className="text-slate-600 mt-1">Send SMS alerts to subscribers about maintenance or system issues</p>
        </div>
        <div className="flex gap-2">
          {/* Test SMS Button - Admin only */}
          {isAdmin && (
            <Dialog open={isTestSmsOpen} onOpenChange={setIsTestSmsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-green-200 text-green-600 hover:bg-green-50" data-testid="test-sms-btn">
                  <CheckCircle size={20} className="mr-2" />
                  Test SMS
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="test-sms-dialog">
                <DialogHeader>
                  <DialogTitle>Send Test SMS</DialogTitle>
                  <DialogDescription>
                    Send a test message to verify SMS is working correctly
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleTestSms} className="space-y-4">
                  <div>
                    <Label htmlFor="test_phone">Phone Number *</Label>
                    <Input
                      id="test_phone"
                      value={testSmsData.phone_number}
                      onChange={(e) => setTestSmsData({ ...testSmsData, phone_number: e.target.value })}
                      placeholder="e.g., 09171234567"
                      required
                      data-testid="test-phone-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Enter your phone number to receive the test message</p>
                  </div>
                  <div>
                    <Label htmlFor="test_message">Test Message</Label>
                    <textarea
                      id="test_message"
                      value={testSmsData.message}
                      onChange={(e) => setTestSmsData({ ...testSmsData, message: e.target.value })}
                      rows="3"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      data-testid="test-message-input"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setIsTestSmsOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-green-600 hover:bg-green-700"
                      disabled={sendingTest}
                      data-testid="send-test-sms-btn"
                    >
                      {sendingTest ? 'Sending...' : 'Send Test SMS'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
          
          {canSendAnnouncements && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="send-announcement-btn">
                <Send size={20} className="mr-2" />
                Send Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="announcement-dialog">
              <DialogHeader>
                <DialogTitle>Send SMS Announcement</DialogTitle>
                <DialogDescription>
                  Broadcast a message to your subscribers via SMS
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title (for your reference) *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., System Maintenance Alert"
                  required
                  data-testid="announcement-title-input"
                />
              </div>
              <div>
                <Label htmlFor="message">SMS Message *</Label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="e.g., We will be performing system maintenance on Jan 5, 2025 from 2AM-4AM. Internet service may be temporarily interrupted. Thank you for your patience."
                  required
                  rows="6"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="announcement-message-input"
                  maxLength="320"
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-slate-500">{formData.message.length}/320 characters</p>
                  <button
                    type="button"
                    onClick={() => setFormData({ 
                      ...formData, 
                      message: formData.message + (formData.message ? '\n\n' : '') + 'For concerns, visit: https://mipay.uk/customer-support'
                    })}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    + Add Support Link
                  </button>
                </div>
                <p className="text-xs text-green-600 mt-1">💡 Tip: You can include links like https://mipay.uk/customer-support in your message</p>
              </div>
              <div>
                <Label htmlFor="send_to">Send To *</Label>
                <Select value={formData.send_to} onValueChange={(value) => {
                  setFormData({ ...formData, send_to: value });
                  if (value !== 'selected') {
                    setSelectedSubscribers([]);
                  }
                }}>
                  <SelectTrigger data-testid="announcement-send-to-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subscribers</SelectItem>
                    <SelectItem value="active">Active Subscribers Only</SelectItem>
                    <SelectItem value="overdue">Overdue Subscribers Only</SelectItem>
                    <SelectItem value="selected">Select Specific Subscribers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Subscriber Selection */}
              {formData.send_to === 'selected' && (
                <div className="space-y-3">
                  <Label>Select Subscribers</Label>
                  
                  {/* Selected subscribers tags */}
                  {selectedSubscribers.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      {selectedSubscribers.map(sub => (
                        <span key={sub.id} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-full text-sm">
                          {sub.name}
                          <button 
                            type="button" 
                            onClick={() => removeSubscriber(sub.id)}
                            className="hover:bg-blue-700 rounded-full p-0.5"
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Search box */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      type="text"
                      placeholder="Search by name or mobile..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Subscriber list */}
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {filteredSubscribers.length > 0 ? (
                      filteredSubscribers.map(subscriber => (
                        <label 
                          key={subscriber.id}
                          className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSubscribers.some(s => s.id === subscriber.id)}
                            onChange={() => toggleSubscriber(subscriber)}
                            className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{subscriber.name}</p>
                            <p className="text-sm text-slate-500">{subscriber.mobile}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            subscriber.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {subscriber.status}
                          </span>
                        </label>
                      ))
                    ) : (
                      <p className="p-4 text-center text-slate-500">No subscribers found</p>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-600">
                    {selectedSubscribers.length} subscriber(s) selected
                  </p>
                </div>
              )}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-amber-600 mt-0.5" size={18} />
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Important</p>
                    <p className="text-sm text-amber-700">This will send an SMS to all selected subscribers via Semaphore. SMS charges may apply.</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={sending}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700" 
                  disabled={sending}
                  data-testid="submit-announcement-btn"
                >
                  {sending ? 'Sending...' : 'Send SMS to Subscribers'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Send className="text-white" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Broadcast Messaging</h3>
            <p className="text-sm text-blue-700">
              Use this feature to notify subscribers about:
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
              <li>Scheduled maintenance windows</li>
              <li>System outages or technical issues</li>
              <li>Service upgrades or improvements</li>
              <li>Important announcements</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Announcements History */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-jakarta font-bold text-slate-900">Announcement History</h3>
          <p className="text-sm text-slate-600 mt-1">Past broadcast messages sent to subscribers</p>
        </div>
        <div className="divide-y divide-slate-200">
          {announcements.length > 0 ? (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="p-6 hover:bg-slate-50 transition-colors"
                data-testid={`announcement-${announcement.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-slate-900">{announcement.title}</h4>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                        {getSendToLabel(announcement.sent_to, announcement.recipient_names)}
                      </span>
                    </div>
                    <p className="text-slate-700 mb-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
                      {announcement.message}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users size={16} />
                        {announcement.recipient_count} recipients
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={16} />
                        {new Date(announcement.created_at).toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle size={16} className="text-green-600" />
                        Sent successfully
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Send size={48} className="mx-auto mb-3 text-slate-300" />
              <p>No announcements sent yet</p>
              <p className="text-sm mt-1">Create your first broadcast message to notify subscribers</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Announcements;
